'use client'

import { AddIconNoCircle } from '@repo/ui/AddIconNoCircle'
import { Avatar } from '@repo/ui/Avatar'
import { DataGridPro, GridColDef, gridClasses } from '@mui/x-data-grid-pro'
import { MoreMenuCard } from '@repo/case-type-mapping/MoreMenuCard'
import { useEffect, useState } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useNavigate } from 'react-router-dom'
import { useThirdPartyCaseTypesStore } from '@repo/store/useThirdPartyCaseTypesStore'
import { v4 as uuidv4 } from 'uuid'
import MUIButton from '@mui/material/Button'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

export const CaseTypeMappingModule = () => {
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { newMatterDefaults, fetchNewMatterDefaults } = useThirdPartyCaseTypesStore()

  const [loading, setLoading] = useState(false)
  const [runFetch, setRunFetch] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    const urlObj = new URL(window.location.href)
    setRunFetch(urlObj.searchParams.get('formComplete') === 'true')

    const getTableData = async () => {
      setLoading(true)
      await fetchNewMatterDefaults({ sourceId: 'PERFECTPORTAL' })
      setRunFetch(false)
      setLoading(false)
    }

    if (!runFetch) setLoading(false)

    getTableData()
  }, [fetchNewMatterDefaults, runFetch, window.location.href])

  const handleAddNewCaseType = async () => {
    navigate('?wizard=PerfectPortal-CaseType&stage=perfect-portal-case-type&step=add-new-case-type-details')
  }

  const AddCaseTypeButton = () => {
    return (
      <MUIButton
        onClick={() => handleAddNewCaseType()}
        sx={{
          border: '1px solid #3D517B',
          borderRadius: '4px',
          fontSize: '0.75rem',
          left: 'calc(100% - 120px)',
          mb: '1.25rem',
          pb: '4px',
          pl: '4px',
          position: 'relative',
          pr: '8px',
          pt: '4px',
          textTransform: 'uppercase',
          width: '103px'
        }}
      >
        <AddIconNoCircle
          fill="#3D517B"
          sx={{
            height: '20px',
            mb: '1px',
            mr: '4px',
            width: '20px'
          }}
        />
        Add new
      </MUIButton>
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'sourceWorkTypeName',
      flex: 1,
      headerName: 'Perfect Portal case type',
      headerClassName: 'perfect-portal-data-grid-header case-type-header',
      width: 224,
      minWidth: 224,
      cellClassName: 'perfect-portal-case-type',
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <MUITooltip
          placement="bottom"
          arrow
          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
          title={params.value}
        >
          <MUITypography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              mb: 0,
              width: 'max-content',
              position: 'relative',
              top: '1rem'
            }}
          >
            {params.value}
          </MUITypography>
        </MUITooltip>
      )
    },
    {
      field: 'workTypeId',
      flex: 1,
      headerName: 'Osprey work type',
      headerClassName: 'perfect-portal-data-grid-header',
      width: 224,
      minWidth: 224,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <MUITooltip
            placement="bottom"
            arrow
            sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
            title={params.row.workTypeDescription}
          >
            <MUITypography
              variant="body2"
              sx={{
                cursor: 'pointer',
                backgroundColor: '#ECEEF2',
                borderRadius: '1rem',
                mb: 0,
                padding: '0.25rem 0.5rem',
                width: 'max-content',
                position: 'relative',
                top: '0.85rem'
              }}
            >
              {params.row.workTypeDescription}
            </MUITypography>
          </MUITooltip>
        )
      }
    },
    {
      field: 'feeEarnerId',
      flex: 1,
      headerName: 'Fee earner',
      headerClassName: 'perfect-portal-data-grid-header',
      width: 224,
      minWidth: 224,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <MUIStack
          display="flex"
          direction="row"
          sx={{ justifyContent: 'flex-start', alignItems: 'center', mb: 0, minHeight: '100%' }}
        >
          {params.value === null ? (
            <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
              <Avatar name="" sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }} />
              <MUITypography variant="body2" sx={{ mt: '8px', fontSize: '0.875rem' }}>
                -
              </MUITypography>
            </MUIStack>
          ) : (
            <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
              <Avatar
                feeEarnerId={params.row.feeEarnerId}
                name={params.row.feeEarnerDescription}
                sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
              />
              <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
                {params.row.feeEarnerDescription}
              </MUITypography>
            </MUIStack>
          )}
        </MUIStack>
      )
    },
    {
      field: 'supervisingFeeEarnerId',
      flex: 1,
      headerName: 'Supervising fee earner',
      headerClassName: 'perfect-portal-data-grid-header',
      width: 224,
      minWidth: 224,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <MUIStack
          display="flex"
          direction="row"
          sx={{ justifyContent: 'flex-start', alignItems: 'center', mb: 0, minHeight: '100%' }}
        >
          {params.value === null ? (
            <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
              <Avatar name="" sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }} />
              <MUITypography variant="body2" sx={{ mt: '8px', fontSize: '0.875rem' }}>
                -
              </MUITypography>
            </MUIStack>
          ) : (
            <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
              <Avatar
                name={params.row.supervisingFeeEarnerDescription}
                feeEarnerId={params.row.supervisingFeeEarnerId}
                sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
              />
              <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
                {params.row.supervisingFeeEarnerDescription}
              </MUITypography>
            </MUIStack>
          )}
        </MUIStack>
      )
    },
    {
      field: 'matterDescription',
      flex: 1,
      headerName: 'Matter description',
      headerClassName: 'perfect-portal-data-grid-header',
      width: 224,
      minWidth: 224,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <MUITooltip
          placement="bottom"
          arrow
          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
          title={params.value}
        >
          <MUITypography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              mb: 0,
              width: 'max-content',
              position: 'relative',
              top: '1rem'
            }}
          >
            {params.value}
          </MUITypography>
        </MUITooltip>
      )
    },
    {
      field: 'departmentDescription',
      flex: 1,
      headerName: 'Department',
      headerClassName: 'perfect-portal-data-grid-header',
      width: 200,
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <MUITooltip
          placement="bottom"
          arrow
          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
          title={params.value}
        >
          <MUITypography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              mb: 0,
              width: 'max-content',
              position: 'relative',
              top: '1rem'
            }}
          >
            {params.value}
          </MUITypography>
        </MUITooltip>
      )
    },
    {
      field: 'branchDescription',
      flex: 1,
      headerName: 'Branch',
      headerClassName: 'perfect-portal-data-grid-header',
      width: 250,
      minWidth: 250,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <MUITooltip
          placement="bottom"
          arrow
          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
          title={params.value}
        >
          <MUITypography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              mb: 0,
              width: 'max-content',
              position: 'relative',
              top: '1rem'
            }}
          >
            {params.value}
          </MUITypography>
        </MUITooltip>
      )
    },
    {
      field: 'notificationRecipient',
      flex: 1,
      headerName: 'Notification recipient',
      headerClassName: 'perfect-portal-data-grid-header',
      width: 300,
      minWidth: 300,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const notificationRecipients = params.row.notificationRecipients as Array<{
          userId: string
          emailAddress?: string
        }>

        if (Array.isArray(notificationRecipients) && notificationRecipients.length > 0) {
          return (
            <MUITooltip
              key={`${notificationRecipients[0].emailAddress}-${notificationRecipients[0].userId}`}
              placement="bottom"
              arrow
              sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
              title={notificationRecipients.map((recipient: any) => (
                <MUIStack key={recipient} sx={{ mb: 2, mt: 2, ml: 1, mr: 1, cursor: 'pointer' }}>
                  <MUITypography variant="body2" color="white" sx={{ p: 0, fontSize: '0.875rem', cursor: 'pointer' }}>
                    {recipient.userId ?? '-'}
                  </MUITypography>
                  <MUITypography variant="body2" color="white" sx={{ p: 0, fontSize: '0.875rem', cursor: 'pointer' }}>
                    {recipient.emailAddress ?? '-'}
                  </MUITypography>
                </MUIStack>
              ))}
            >
              <MUIStack
                display="flex"
                direction="column"
                sx={{
                  cursor: 'pointer',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  mb: 0,
                  minHeight: '100%',
                  padding: '0.5rem'
                }}
              >
                <MUITypography
                  variant="body2"
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    mb: 0
                  }}
                >
                  {notificationRecipients.length > 1
                    ? `${notificationRecipients[0].userId} +${notificationRecipients.length - 1}`
                    : (notificationRecipients[0].userId ?? '-')}
                </MUITypography>
                <MUITypography
                  variant="body2"
                  sx={{
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    mb: 0
                  }}
                >
                  {notificationRecipients.length > 1
                    ? `${notificationRecipients[0].emailAddress} +${notificationRecipients.length - 1}`
                    : (notificationRecipients[0].emailAddress ?? '-')}
                </MUITypography>
              </MUIStack>
            </MUITooltip>
          )
        } else {
          return (
            <MUITypography variant="body2" sx={{ fontSize: '0.875rem' }}>
              -
            </MUITypography>
          )
        }
      }
    },
    {
      field: 'moreMenu',
      headerName: '',
      width: 49,
      hideable: false,
      filterable: false,
      sortable: false,
      type: 'actions',
      renderCell: (params: any) => <MoreMenuCard rowData={params.row} setRunFetch={setRunFetch} />
    }
  ]

  return (
    <MUIPaper
      data-testid="CaseTypeMappingModule"
      sx={{
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)',
        fontWeight: 400,
        height: 'auto',
        mt: pageTitleBlockIsVisible ? 0 : '12rem',
        width: '100%',
        [`& .perfect-portal-data-grid-header`]: {
          fontWeight: 900,
          [`&.case-type-header`]: {
            borderRight: '1px solid #e0e0e0',
            [`& .MuiDataGrid-columnSeparator`]: {
              display: 'none'
            }
          }
        },
        [`& .perfect-portal-case-type`]: {
          borderRight: '1px solid #e0e0e0',
          fontWeight: 900
        },
        [`& .MuiDataGrid-footerContainer`]: {
          display: 'none'
        }
      }}
    >
      <DataGridPro
        columns={columns}
        loading={loading}
        disableColumnSorting
        disableColumnFilter
        disableColumnSelector
        disableColumnMenu={true}
        disableVirtualization
        getRowId={(row) => `${row.newMatterDefaultId}-${uuidv4()}`}
        pinnedColumns={{ left: ['sourceWorkTypeName'], right: ['moreMenu'] }}
        rows={newMatterDefaults || []}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          }
        }}
        slots={{
          headerFilterMenu: null,
          toolbar: () => <AddCaseTypeButton />
        }}
        sx={{
          borderTop: 'none',
          [`.MuiDataGrid-root`]: { border: 'none', height: 'auto' },
          [`& .Mui-selected`]: { backgroundColor: 'transparent !important' },
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' },
          [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' }
        }}
      />
    </MUIPaper>
  )
}
